import { apiCall } from "../../../../_services/apiCall";
import config from "../../../../config/config";

const BASE = config.POSTLOGIN_API_URL_COMPANY;

/**
 * Fetches training categories with the number of active trainings tagged with each
 * in the given financial year. Backs the top-level category grid. Returns null on a
 * failed request so the caller can tell an error apart from a genuinely empty list.
 * @param {number} financialYearId
 * @returns {Promise<Array<{id:number,title:string,trainingCount:number}>|null>}
 */
export const getCoverageCategories = async (financialYearId) => {
  if (!financialYearId) return [];
  const { isSuccess, data } = await apiCall(
    `${BASE}training-dashboard/categories`,
    {},
    { financialYearId },
    "GET"
  );
  if (!isSuccess) return null;
  return Array.isArray(data?.data) ? data.data : [];
};

/**
 * Fetches a paginated page of active trainings in a category, grouped by title so a
 * program run on several dates is one row spanning all its sessions. The free-text
 * search term is URL-encoded here because apiCall builds the query string without
 * encoding and titles contain spaces and "&"; the server decodes it automatically.
 * @param {number} categoryId
 * @param {number} financialYearId
 * @param {number} limit
 * @param {number} offset
 * @param {string} search
 * @returns {Promise<{items:Array,total:number,limit:number,offset:number}|null>} null on a failed request
 */
export const getCoverageTrainings = async (categoryId, financialYearId, limit = 20, offset = 0, search = "") => {
  const empty = { items: [], total: 0, limit, offset };
  if (!categoryId || !financialYearId) return empty;
  const params = { financialYearId, limit, offset };
  const term = (search || "").trim();
  if (term) params.search = encodeURIComponent(term);
  const { isSuccess, data } = await apiCall(
    `${BASE}training-dashboard/categories/${categoryId}/trainings`,
    {},
    params,
    "GET"
  );
  if (!isSuccess) return null;
  return data?.data || empty;
};

/**
 * Fetches a grouped training's detail: the principles covered, the combined attendee
 * split across all its sessions, and a per-session (per date-range) split.
 * @param {number[]} trainingIds - the session ids of one grouped program
 * @returns {Promise<{trainingTitle:string,principles:string[],combined:{totalAttendees:number,split:Array},sessions:Array}|null>}
 */
export const getTrainingCoverageDetail = async (trainingIds) => {
  if (!trainingIds || !trainingIds.length) return null;
  const { isSuccess, data } = await apiCall(
    `${BASE}training-dashboard/training-detail`,
    {},
    { trainingIds },
    "GET"
  );
  return isSuccess && data?.data ? data.data : null;
};
